import { useMutation, useQueryClient } from '@tanstack/react-query';
import { plantSourceApi } from '../lib/plantSourceApi';
import { CreatePlantSourceInput, UpdatePlantSourceInput } from '../types/plantSource';

export function useCreatePlantSource() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sourceData: CreatePlantSourceInput) =>
      plantSourceApi.createPlantSource(sourceData),
    onSuccess: () => {
      // Invalidate all plant source queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['plantSources'] });
    },
  });
}

export function useUpdatePlantSource() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdatePlantSourceInput }) =>
      plantSourceApi.updatePlantSource(id, updates),
    onSuccess: updatedSource => {
      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: ['plantSources'] });

      // Update the specific plant source in cache (optimistic update)
      queryClient.setQueryData(['plantSource', updatedSource.id], updatedSource);
    },
  });
}

export function useDeletePlantSource() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sourceId: string) => plantSourceApi.deletePlantSource(sourceId),
    onSuccess: (_, sourceId) => {
      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: ['plantSources'] });

      // Remove the specific plant source from cache
      queryClient.removeQueries({ queryKey: ['plantSource', sourceId] });
    },
  });
}

export function useGeneratePlantSourceQRCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sourceId: string) => plantSourceApi.generateQRCode(sourceId),
    onSuccess: (data, sourceId) => {
      // Invalidate the specific plant source to refresh its QR code data
      queryClient.invalidateQueries({ queryKey: ['plantSource', sourceId] });

      // Also invalidate the list to ensure it reflects QR code status
      queryClient.invalidateQueries({ queryKey: ['plantSources'] });
    },
  });
}
